import React, { useEffect, useState, useCallback } from "react";
import DataSourceBusinessLogicFailuresWidget from "../../../components/Widgets/DataSourceWidgets/DataSourceBusinessLogicFailuresWidget";
import DataSourceColumnFailuresWidget from "../../../components/Widgets/DataSourceWidgets/DataSourceColumnFailuresWidget";
import SplashLoader from "../../../components/Loaders/SplashLoader";
import CTAMessage from "../../../components/Help/CTAMessage";
import ErrorMessages from "../../../components/Notifications/ErrorMessages";
import styled from "styled-components/macro";
import DataSourceFailureDetails from "../../../components/Widgets/DataSourceWidgets/DataSourceFailureDetails";
import DataSourceRuleInstanceHistory from "../../../components/Widgets/DataSourceWidgets/DataSourceRuleInstanceHistory";
import DataSourceFailuresHistory from "../../../components/Widgets/DataSourceWidgets/DataSourceFailuresHistory";
import DataQualitySpotlight from "../../../components/Widgets/DataSourceWidgets/DataQualitySpotlight";
import ReportScoreOverview from "./ReportScoreOverview";
import SmallToggle from "../../../components/Toggle/SmallToggle";

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(265px, 1fr));
  grid-auto-rows: dense;
  grid-gap: 1rem;
  margin-bottom: 1rem;
  position: relative;
`;

const DashboardGridTop = styled.div`
  display: flex;
  margin-bottom: 1rem;
  @media (max-width: 1080px) {
    flex-direction: column;
  }
`;

const SpotlightContainer = styled.div`
  width: 400px;
  margin-right: 1rem;
  @media (max-width: 1080px) {
    width: auto;
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const Report = ({
  loading,
  errors,
  sourceId,
  reportData,
  isGeneratingCachedReport,
  dataSourceName,
  errorsLatest,
  reportId,
  fetchLatestReport,
  dataSource,
}) => {
  const [ruleInstanceDisplay, setRuleInstanceDisplay] = useState({
    bar: null,
    type: "Historical",
  });
  const [view, setView] = useState("Historical");
  const [spotlightView, setSpotlightView] = useState("Policy");

  const setRule = useCallback(
    (data) => {
      const barToSet = data?.bar;

      setRuleInstanceDisplay((prevData) => {
        if (!barToSet) return { bar: null };
        return data;
      });
    },
    [setRuleInstanceDisplay]
  );

  const toggleData = [
    {
      name: "Historical",
      action: () => setView("Historical"),
    },
    {
      name: "Details",
      action: () => setView("Details"),
    },
  ];

  //Show loading of report
  if (loading) return <SplashLoader text="Loading Report" />;

  const failureCount = reportData?.totalFailures ?? null;
  const failureData =
    reportData?.ruleFailures?.failures
      .filter((f) => f.failureCount)
      .sort((a, b) => b.failureCount - a.failureCount)
      .slice(0, 10) ?? [];

  const failureHistory = reportData?.failuresHistory ?? null;
  const failureDetails = reportData?.failureDetails ?? null;
  const columnFailures =
    reportData?.failureDetails?.columnEntries
      .filter((c) => c.failureCount)
      .sort((a, b) => b.failureCount - a.failureCount)
      .slice(0, 10) ?? [];

  if (errors)
    return (
      <CTAMessage
        msg={
          <div>
            <ErrorMessages errors={errors} />
          </div>
        }
      />
    );

  if (errorsLatest)
    return (
      <CTAMessage
        msg={
          <div>
            <ErrorMessages errors={errorsLatest} />
          </div>
        }
      />
    );

  return (
    <>
      <ReportScoreOverview
        apiData={reportData}
        sourceId={sourceId}
        loading={loading}
      />

      {/* WIDGETS Upto 4 */}
      <DashboardGridTop>
        <SpotlightContainer>
          <DataQualitySpotlight
            sourceId={sourceId}
            loading={loading}
            data={failureDetails}
            spotlightView={spotlightView}
            setSpotlightView={setSpotlightView}
          />
        </SpotlightContainer>
        <div style={{ flex: 1 }}>
          {spotlightView === "Policy" ? (
            <DataSourceBusinessLogicFailuresWidget
              sourceId={sourceId}
              loading={loading}
              data={failureData}
              dataSourceName={dataSourceName}
              setRuleInstanceDisplay={setRule}
              dataSource={dataSource}
            />
          ) : (
            <DataSourceColumnFailuresWidget
              sourceId={sourceId}
              loading={loading}
              data={columnFailures}
              dataSourceName={dataSourceName}
            />
          )}
        </div>
      </DashboardGridTop>

      <DashboardGrid>
        {ruleInstanceDisplay?.bar && (
          <div
            style={{
              position: "absolute",
              right: 0,
              zIndex: 2,
              padding: "1rem",
            }}
          >
            <SmallToggle toggleState={view} toggleData={toggleData} />
          </div>
        )}

        {ruleInstanceDisplay?.bar && view === "Details" && (
          <DataSourceFailureDetails
            sourceId={sourceId}
            loading={loading}
            data={ruleInstanceDisplay}
            failureCount={failureCount}
          />
        )}

        {ruleInstanceDisplay?.bar && view === "Historical" && (
          <DataSourceRuleInstanceHistory
            sourceId={sourceId}
            reportId={reportId}
            loading={loading}
            barData={ruleInstanceDisplay}
          />
        )}

        {ruleInstanceDisplay?.bar ? null : (
          <DataSourceFailuresHistory
            sourceId={sourceId}
            loading={loading}
            data={failureHistory}
          />
        )}
      </DashboardGrid>
    </>
  );
};

export default Report;
